import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteCim,
  getEmployment,
  saveEmployment,
  uploadCim,
  type SaveEmploymentInput,
} from '../lib/employmentApi';
import {
  cancelPayroll,
  confirmPayroll,
  getPersonPayroll,
  previewPayroll,
  type ConfirmPayrollInput,
} from '../lib/payrollApi';

// React-query hooks for the Angajare tab (employment + payroll), following the
// usePeople.ts conventions: person-scoped keys under ['person', id, ...].

export function useEmployment(personId: string | undefined) {
  return useQuery({
    queryKey: ['person', personId, 'employment'],
    queryFn: () => getEmployment(personId as string),
    enabled: Boolean(personId),
  });
}

export function useSaveEmployment(personId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SaveEmploymentInput) => saveEmployment(personId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['person', personId, 'employment'] });
    },
  });
}

export function useUploadCim(personId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadCim(personId, file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['person', personId, 'employment'] });
    },
  });
}

export function useDeleteCim(personId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => deleteCim(personId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['person', personId, 'employment'] });
    },
  });
}

// --- Payroll -----------------------------------------------------------------

export function usePersonPayroll(personId: string | undefined) {
  return useQuery({
    queryKey: ['person', personId, 'payroll'],
    queryFn: () => getPersonPayroll(personId as string),
    enabled: Boolean(personId),
  });
}

export function usePreviewPayroll() {
  return useMutation({
    mutationFn: ({ file, year, month }: { file: File; year?: number; month?: number }) =>
      previewPayroll(file, { year, month }),
  });
}

export function useConfirmPayroll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, input }: { documentId: string; input: ConfirmPayrollInput }) =>
      confirmPayroll(documentId, input),
    onSuccess: () => {
      // Entries may span many people — invalidate the whole person prefix.
      void qc.invalidateQueries({ queryKey: ['person'] });
    },
  });
}

export function useCancelPayroll() {
  return useMutation({
    mutationFn: (documentId: string) => cancelPayroll(documentId),
  });
}
